import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  HStack,
  VStack,
  SimpleGrid,
  Button,
  IconButton,
  Badge,
  Spinner,
  Divider,
  Input,
  Textarea,
  Select,
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
  Checkbox,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { FiZap, FiPlay, FiClock } from 'react-icons/fi';
import { useQuery } from 'react-query';
import { automationsApi, collectionsApi, iotApi, AutomationRun } from '../services/api';

const emptyForm = {
  name: '',
  trigger_type: 'whatsapp',
  target_collection: '',
  prompt: '',
  delivery_type: 'whatsapp',
  delivery_target: '',
  delivery_instance: '',
  chats: [] as string[],
  keywords: '',
  cadence: 'daily',
  hour: 8,
  minute: 0,
  weekday: 1,
  device_id: '',
  metric: '',
  operator: '>',
  threshold: '',
  unit: '',
};

const Automations = () => {
  const toast = useToast();
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [newBase, setNewBase] = useState(false);   // criar base nova vs escolher existente
  const [convSearch, setConvSearch] = useState(''); // busca na lista de conversas

  const { data: automations = [], refetch, isLoading } = useQuery('automations', automationsApi.list);
  const { data: collections = [] } = useQuery('collections-for-auto', collectionsApi.list);
  const { data: conversations = [] } = useQuery('wa-convs', automationsApi.whatsappConversations, {
    enabled: form.trigger_type === 'whatsapp',
  });
  const { data: iotConfig } = useQuery('iot-config', iotApi.config, { enabled: form.trigger_type === 'iot' });

  // Histórico de execuções (modal).
  const history = useDisclosure();
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [historyName, setHistoryName] = useState('');
  const openHistory = async (id: string, name: string) => {
    setHistoryName(name);
    history.onOpen();
    try {
      setRuns(await automationsApi.runs(id));
    } catch {
      setRuns([]);
    }
  };

  const create = async () => {
    const needsTarget = form.delivery_type !== 'document';
    if (!form.name.trim() || !form.prompt.trim() || !form.target_collection || (needsTarget && !form.delivery_target.trim())) {
      toast({ title: 'Preencha nome, base, prompt e destino', status: 'warning', duration: 2500 });
      return;
    }
    setSaving(true);
    try {
      let trigger_config: any = {};
      if (form.trigger_type === 'whatsapp') {
        trigger_config = { chats: form.chats, keywords: form.keywords.split(',').map((k) => k.trim()).filter(Boolean) };
      } else if (form.trigger_type === 'schedule') {
        trigger_config = { cadence: form.cadence, hour: Number(form.hour), minute: Number(form.minute), weekday: Number(form.weekday) };
      } else if (form.trigger_type === 'iot') {
        trigger_config = { device_id: form.device_id.trim(), metric: form.metric.trim(), operator: form.operator, threshold: Number(form.threshold), unit: form.unit.trim() };
      }
      const res = await automationsApi.create({
        name: form.name,
        trigger_type: form.trigger_type,
        target_collection: form.target_collection,
        prompt: form.prompt,
        delivery_type: form.delivery_type,
        delivery_target: form.delivery_target,
        delivery_instance: form.delivery_instance,
        trigger_config,
      });
      setForm({ ...emptyForm });
      setNewBase(false);
      setConvSearch('');
      refetch();
      if (res?.webhook_url) {
        window.prompt('Automação criada! Copie a URL do webhook e configure no seu sistema:', res.webhook_url);
      } else {
        toast({ title: 'Automação criada', status: 'success', duration: 2500 });
      }
    } catch (e: any) {
      toast({ title: 'Não foi possível criar', description: e?.response?.data?.detail || 'Tente novamente.', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const run = async (id: string) => {
    setRunningId(id);
    try {
      const r = await automationsApi.run(id);
      toast({ title: 'Executada e entregue', description: r.preview?.slice(0, 120), status: 'success', duration: 5000, isClosable: true });
      refetch();
    } catch (e: any) {
      toast({ title: 'Falha ao executar', description: e?.response?.data?.detail, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setRunningId(null);
    }
  };

  const toggle = async (id: string, enabled: boolean) => {
    await automationsApi.toggle(id, enabled);
    refetch();
  };

  const remove = async (id: string, name: string) => {
    if (!window.confirm(`Excluir a automação "${name}"?`)) return;
    await automationsApi.remove(id);
    refetch();
  };

  return (
    <Box>
      <Heading size="lg" color="brand.700" mb={1}>Automações</Heading>
      <Text color="gray.600" mb={6}>
        Quando algo acontece (nova mensagem de WhatsApp), a IA processa o que você pedir sobre uma coleção e
        entrega o resultado no canal escolhido. Você também pode rodar manualmente para testar.
      </Text>

      {/* Criar */}
      <Box borderWidth="1px" borderRadius="lg" bg="white" p={5} shadow="sm" mb={8}>
        <Heading size="sm" mb={4} color="gray.700">Nova automação</Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Nome</FormLabel>
            <Input size="sm" placeholder="Ex.: Resumo diário do comercial" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Gatilho</FormLabel>
            <Select size="sm" value={form.trigger_type} onChange={(e) => setForm({ ...form, trigger_type: e.target.value })}>
              <option value="whatsapp">Nova mensagem de WhatsApp</option>
              <option value="schedule">Agendado (horário fixo)</option>
              <option value="database">Novo dado no banco (integração SQL)</option>
              <option value="file">Novo arquivo (Drive / SharePoint / S3)</option>
              <option value="webhook">Webhook (outro sistema dispara)</option>
              <option value="iot">Sensor / IoT (leitura passa de um limite)</option>
              <option value="manual">Manual (só quando eu clicar)</option>
            </Select>
          </FormControl>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Base de conhecimento (sobre o que a IA responde)</FormLabel>
            {newBase ? (
              <Input size="sm" placeholder="Nome da nova base (ex.: comercial)"
                value={form.target_collection} onChange={(e) => setForm({ ...form, target_collection: e.target.value })} />
            ) : (
              <Select size="sm" placeholder="Selecione uma base"
                value={form.target_collection}
                onChange={(e) => {
                  if (e.target.value === '__new__') { setNewBase(true); setForm({ ...form, target_collection: '' }); }
                  else setForm({ ...form, target_collection: e.target.value });
                }}>
                {collections.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                <option value="__new__">➕ Criar nova base...</option>
              </Select>
            )}
            <FormHelperText fontSize="xs">
              {newBase
                ? <>Base nova — vai ser alimentada por uploads ou integrações. <Text as="span" color="brand.600" cursor="pointer" onClick={() => { setNewBase(false); setForm({ ...form, target_collection: '' }); }}>usar uma existente</Text></>
                : 'A IA processa o conteúdo desta base. Você pode criar uma nova.'}
            </FormHelperText>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Entrega</FormLabel>
            <Select size="sm" value={form.delivery_type} onChange={(e) => setForm({ ...form, delivery_type: e.target.value })}>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">E-mail</option>
              <option value="webhook">Webhook (POST para outro sistema)</option>
              <option value="document">Salvar como documento na base</option>
            </Select>
          </FormControl>
          {form.delivery_type !== 'document' && (
            <FormControl isRequired>
              <FormLabel fontSize="sm">{
                form.delivery_type === 'email' ? 'E-mail de destino'
                  : form.delivery_type === 'webhook' ? 'URL de destino (POST)'
                  : 'Número WhatsApp (55DDD...)'
              }</FormLabel>
              <Input size="sm" value={form.delivery_target} onChange={(e) => setForm({ ...form, delivery_target: e.target.value })} />
              <FormHelperText fontSize="xs">Para onde enviar o resultado da automação.</FormHelperText>
            </FormControl>
          )}
          {form.delivery_type === 'whatsapp' && (
            <FormControl>
              <FormLabel fontSize="sm">WhatsApp de envio (opcional)</FormLabel>
              <Input size="sm" placeholder="usa o número padrão se vazio" value={form.delivery_instance}
                onChange={(e) => setForm({ ...form, delivery_instance: e.target.value })} />
            </FormControl>
          )}
        </SimpleGrid>

        {/* Configuração específica do gatilho de WhatsApp */}
        {form.trigger_type === 'whatsapp' && (
          <Box mt={4} p={4} bg="gray.50" borderRadius="md">
            <Text fontWeight="semibold" fontSize="sm" mb={3}>Quando disparar (gatilho de WhatsApp)</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Em quais conversas?</FormLabel>
                {conversations.length === 0 ? (
                  <Text fontSize="xs" color="gray.500">
                    Nenhuma conversa disponível. Conecte o WhatsApp e escolha as conversas em Integrações.
                    (Sem seleção = qualquer conversa.)
                  </Text>
                ) : (
                  <>
                    {conversations.length > 5 && (
                      <Input size="xs" mb={2} placeholder="Buscar conversa..." value={convSearch}
                        onChange={(e) => setConvSearch(e.target.value)} />
                    )}
                    <Stack maxH="160px" overflowY="auto" spacing={1}>
                      {conversations
                        .filter((c) => c.label.toLowerCase().includes(convSearch.toLowerCase()))
                        .map((c) => (
                          <Checkbox
                            key={c.jid}
                            size="sm"
                            isChecked={form.chats.includes(c.jid)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...form.chats, c.jid]
                                : form.chats.filter((j) => j !== c.jid);
                              setForm({ ...form, chats: next });
                            }}
                          >
                            {c.label}
                          </Checkbox>
                        ))}
                    </Stack>
                  </>
                )}
                <FormHelperText fontSize="xs">
                  São as conversas escolhidas para ingestão na Integração. Vazio = qualquer uma delas.
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Só quando a mensagem contiver (palavras-chave)</FormLabel>
                <Input size="sm" placeholder="ex.: proposta, orçamento, urgente"
                  value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
                <FormHelperText fontSize="xs">Separe por vírgula. Vazio = qualquer mensagem.</FormHelperText>
              </FormControl>
            </SimpleGrid>
          </Box>
        )}

        {/* Configuração do gatilho Agendado */}
        {form.trigger_type === 'schedule' && (
          <Box mt={4} p={4} bg="gray.50" borderRadius="md">
            <Text fontWeight="semibold" fontSize="sm" mb={3}>Quando rodar</Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Frequência</FormLabel>
                <Select size="sm" value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })}>
                  <option value="hourly">A cada hora</option>
                  <option value="daily">Todo dia</option>
                  <option value="weekly">Toda semana</option>
                </Select>
              </FormControl>
              {form.cadence === 'weekly' && (
                <FormControl>
                  <FormLabel fontSize="sm">Dia da semana</FormLabel>
                  <Select size="sm" value={form.weekday} onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}>
                    {['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'].map((d, i) => (
                      <option key={i} value={i}>{d}</option>
                    ))}
                  </Select>
                </FormControl>
              )}
              {form.cadence !== 'hourly' && (
                <FormControl>
                  <FormLabel fontSize="sm">Horário</FormLabel>
                  <Input size="sm" type="time"
                    value={`${String(form.hour).padStart(2, '0')}:${String(form.minute).padStart(2, '0')}`}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(':');
                      setForm({ ...form, hour: Number(h), minute: Number(m) });
                    }} />
                </FormControl>
              )}
            </SimpleGrid>
          </Box>
        )}

        {/* Configuração do gatilho IoT / Sensor */}
        {form.trigger_type === 'iot' && (
          <Box mt={4} p={4} bg="gray.50" borderRadius="md">
            <Text fontWeight="semibold" fontSize="sm" mb={3}>Quando o sensor passar do limite</Text>
            <SimpleGrid columns={{ base: 1, md: 5 }} spacing={3}>
              <FormControl>
                <FormLabel fontSize="sm">Sensor (id)</FormLabel>
                <Input size="sm" placeholder="ex.: freezer1" value={form.device_id}
                  onChange={(e) => setForm({ ...form, device_id: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Métrica</FormLabel>
                <Input size="sm" placeholder="ex.: temp" value={form.metric}
                  onChange={(e) => setForm({ ...form, metric: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Condição</FormLabel>
                <Select size="sm" value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })}>
                  <option value=">">maior que</option>
                  <option value="<">menor que</option>
                  <option value=">=">maior ou igual</option>
                  <option value="<=">menor ou igual</option>
                  <option value="==">igual a</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Limite</FormLabel>
                <Input size="sm" type="number" placeholder="ex.: -10" value={form.threshold}
                  onChange={(e) => setForm({ ...form, threshold: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Unidade</FormLabel>
                <Input size="sm" placeholder="ex.: C" value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              </FormControl>
            </SimpleGrid>
            {iotConfig?.url && (
              <Box mt={3} fontSize="xs" color="gray.600">
                <Text fontWeight="semibold">URL para seus dispositivos enviarem leituras (POST):</Text>
                <Text fontFamily="mono" wordBreak="break-all">{iotConfig.url}</Text>
                <Text mt={1}>Corpo: {'{'} "device_id": "freezer1", "metric": "temp", "value": -5 {'}'}</Text>
              </Box>
            )}
          </Box>
        )}

        {/* Nota dos gatilhos de fonte de dados (Banco / Arquivo) */}
        {(form.trigger_type === 'database' || form.trigger_type === 'file') && (
          <Box mt={4} p={4} bg="gray.50" borderRadius="md">
            <Text fontSize="sm" color="gray.600">
              {form.trigger_type === 'database'
                ? 'Dispara quando a integração de banco de dados traz registros novos para a base escolhida acima.'
                : 'Dispara quando a integração (Google Drive, SharePoint ou S3) traz arquivos novos para a base escolhida acima.'}
              {' '}Configure a integração em <b>Integrações</b> apontando para esta mesma base.
            </Text>
          </Box>
        )}

        {/* Nota do gatilho Webhook */}
        {form.trigger_type === 'webhook' && (
          <Box mt={4} p={4} bg="gray.50" borderRadius="md">
            <Text fontSize="sm" color="gray.600">
              Ao criar, geramos uma <b>URL secreta</b>. Seu sistema faz um <b>POST</b> nessa URL (com um JSON) e a
              automação roda na hora, usando o conteúdo enviado. A URL aparece para você copiar logo após criar.
            </Text>
          </Box>
        )}

        <FormControl isRequired mt={4}>
          <FormLabel fontSize="sm">O que a IA deve fazer (prompt)</FormLabel>
          <Textarea size="sm" rows={3} placeholder="Ex.: Resuma os principais pontos das novas conversas e liste os próximos passos."
            value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} />
        </FormControl>
        <Button mt={4} colorScheme="brand" size="sm" leftIcon={<FiZap />} onClick={create} isLoading={saving}>
          Criar automação
        </Button>
      </Box>

      <Divider mb={6} />

      {/* Lista */}
      {isLoading ? (
        <Flex justify="center" py={12}><Spinner color="brand.500" /></Flex>
      ) : automations.length === 0 ? (
        <Box borderWidth="1px" borderRadius="lg" borderStyle="dashed" py={12} px={6} textAlign="center">
          <VStack spacing={3}>
            <Flex bg="brand.50" boxSize="64px" borderRadius="full" align="center" justify="center">
              <FiZap size={26} color="#6B46C1" />
            </Flex>
            <Box>
              <Heading size="md" color="gray.700">Crie sua primeira automação</Heading>
              <Text color="gray.500" mt={1} maxW="lg" mx="auto">
                Um gatilho (nova mensagem, novo arquivo ou horário) → a IA processa → entrega no WhatsApp ou e-mail.
              </Text>
            </Box>
          </VStack>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {automations.map((a) => (
            <Box key={a.id} borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm">
              <Flex justify="space-between" align="flex-start">
                <HStack><FiZap /><Text fontWeight="semibold">{a.name}</Text></HStack>
                <HStack>
                  <Switch isChecked={a.enabled} onChange={(e) => toggle(a.id, e.target.checked)} colorScheme="brand" size="sm" />
                  <IconButton aria-label="Excluir" icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red" onClick={() => remove(a.id, a.name)} />
                </HStack>
              </Flex>
              <VStack align="stretch" spacing={1} mt={2} fontSize="sm" color="gray.600">
                <Text><b>Gatilho:</b> {
                  a.trigger_type === 'whatsapp' ? 'Nova mensagem WhatsApp'
                    : a.trigger_type === 'schedule' ? 'Agendado'
                    : a.trigger_type === 'database' ? 'Novo dado no banco'
                    : a.trigger_type === 'file' ? 'Novo arquivo'
                    : a.trigger_type === 'webhook' ? 'Webhook'
                    : a.trigger_type === 'iot' ? 'Sensor / IoT'
                    : 'Manual'
                }</Text>
                {a.trigger_type === 'iot' && (
                  <Text fontSize="xs">
                    {a.trigger_config?.device_id || 'qualquer'} · {a.trigger_config?.metric} {a.trigger_config?.operator} {a.trigger_config?.threshold} {a.trigger_config?.unit}
                  </Text>
                )}
                {a.trigger_type === 'whatsapp' && (
                  <Text fontSize="xs">
                    {(a.trigger_config?.chats?.length || 0) > 0 ? `${a.trigger_config!.chats!.length} conversa(s)` : 'qualquer conversa'}
                    {(a.trigger_config?.keywords?.length || 0) > 0 ? ` · palavras: ${a.trigger_config!.keywords!.join(', ')}` : ''}
                  </Text>
                )}
                {a.trigger_type === 'schedule' && (
                  <Text fontSize="xs">
                    {a.trigger_config?.cadence === 'hourly' ? 'a cada hora'
                      : a.trigger_config?.cadence === 'weekly' ? `toda semana às ${String(a.trigger_config?.hour ?? 8).padStart(2, '0')}:${String(a.trigger_config?.minute ?? 0).padStart(2, '0')}`
                      : `todo dia às ${String(a.trigger_config?.hour ?? 8).padStart(2, '0')}:${String(a.trigger_config?.minute ?? 0).padStart(2, '0')}`}
                  </Text>
                )}
                <Text><b>Entrega:</b> {a.delivery_type} → {a.delivery_target}</Text>
                <Text noOfLines={2}><b>Prompt:</b> {a.prompt}</Text>
                {a.last_status && (
                  <HStack><Text><b>Última:</b></Text>
                    <Badge colorScheme={a.last_status === 'ok' ? 'green' : 'red'}>{a.last_status}</Badge>
                    {a.last_run_at && <Text fontSize="xs" color="gray.400">{new Date(a.last_run_at).toLocaleString('pt-BR')}</Text>}
                  </HStack>
                )}
              </VStack>
              <HStack mt={3}>
                <Button size="xs" leftIcon={<FiPlay />} variant="outline" colorScheme="brand"
                  isLoading={runningId === a.id} onClick={() => run(a.id)}>
                  Rodar agora
                </Button>
                <Button size="xs" leftIcon={<FiClock />} variant="ghost" onClick={() => openHistory(a.id, a.name)}>
                  Histórico
                </Button>
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* Histórico de execuções */}
      <Modal isOpen={history.isOpen} onClose={history.onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Execuções — {historyName}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {runs.length === 0 ? (
              <Text color="gray.500" fontSize="sm">Ainda sem execuções registradas.</Text>
            ) : (
              <VStack align="stretch" spacing={3}>
                {runs.map((r, i) => (
                  <Box key={i} borderWidth="1px" borderRadius="md" p={3}>
                    <HStack justify="space-between" mb={1}>
                      <Badge colorScheme={r.status === 'ok' ? 'green' : 'orange'}>{r.status}</Badge>
                      <Text fontSize="xs" color="gray.400">
                        {r.source} · {r.created_at ? new Date(r.created_at).toLocaleString('pt-BR') : ''}
                      </Text>
                    </HStack>
                    {r.output_preview && <Text fontSize="sm" color="gray.700" noOfLines={4}>{r.output_preview}</Text>}
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Automations;
